import React, { useState, useEffect, useRef } from 'react';
import { Card, Typography, Button, message, Result, Space, Tag, Spin, Alert } from 'antd';
import { QrcodeOutlined, CheckCircleOutlined, ClockCircleOutlined, LogoutOutlined, CameraOutlined } from '@ant-design/icons';
import { Html5Qrcode } from 'html5-qrcode';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

const { Title, Text } = Typography;

// Admins are not tied to the office, so a missing/coarse GPS fix must not stop
// them from scanning. Staff still need a precise fix inside the office radius.
function isGeoExempt() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    if (!u) return false;
    return u.role === 'admin' || u.role === 'super_admin';
  } catch (e) {
    return false;
  }
}

message.config({ maxCount: 1 });

// Attendance must only be possible from the office, so a coarse network/wifi fix
// (which can be kilometres off yet land near the office) is not good enough.
// Watch the position until a genuinely precise GPS reading arrives.
const GOOD_ACCURACY_M = 50;   // stop as soon as we get this
const MAX_ACCURACY_M = 150;   // anything worse is rejected

// Admin can widen a single staff member's attendance radius; the accuracy the
// server accepts scales with it, so mirror that here.
function maxAccuracyForMe() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    const r = Number(u && u.qr_radius_m) || 0;
    return r > 0 ? Math.max(MAX_ACCURACY_M, r * 0.3) : MAX_ACCURACY_M;
  } catch (e) {
    return MAX_ACCURACY_M;
  }
}

function getPreciseLocation(waitMs = 15000) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('Geolocation not supported')); return; }
    let best = null;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      navigator.geolocation.clearWatch(watchId);
      clearTimeout(timer);
      if (best) resolve(best); else reject(new Error('no-fix'));
    };
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const acc = Number(pos.coords.accuracy) || 99999;
        if (!best || acc < best.acc) {
          best = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc, ts: pos.timestamp };
        }
        if (best.acc <= GOOD_ACCURACY_M) finish();
      },
      (err) => { if (!best && err && err.code === 1) { done = true; navigator.geolocation.clearWatch(watchId); clearTimeout(timer); reject(err); } },
      { enableHighAccuracy: true, timeout: waitMs, maximumAge: 0 }
    );
    const timer = setTimeout(finish, waitMs);
  });
}

export default function QrScan() {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');
  const [scannerPending, setScannerPending] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const scannerRef = useRef(null);
  const readerRef = useRef(null);
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token');
  const navigate = useNavigate();

  // Auto check-in if token is in URL (staff clicked QR link)
  useEffect(() => {
    if (urlToken && !result) {
      (async () => {
        setLoading(true);
        let loc = null;
        try {
          loc = await getPreciseLocation();
        } catch (e) { /* backend will reject a scan without location */ }
        try {
          const res = await api.post('/staff/qr-checkin', loc
            ? { token: urlToken, lat: loc.lat, lng: loc.lng, acc: loc.acc }
            : { token: urlToken });
          setResult(res.data);
          message.destroy();
          if (res.data.already) message.info(res.data.message);
          else message.success(res.data.message);
        } catch (err) {
          message.destroy();
          message.error(err.response?.data?.error || 'Failed to check in');
          setResult({ error: err.response?.data?.error || 'Failed' });
        } finally { setLoading(false); }
      })();
    }
  }, [urlToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const startScan = async () => {
    setResult(null);
    setCameraError(null);
    setDebugInfo('');
    setLocationError(null);

    // Step 0: Get a precise GPS location (office-only attendance)
    const geoExempt = isGeoExempt();
    try {
      const loc = await getPreciseLocation();
      if (loc.acc > maxAccuracyForMe() && !geoExempt) {
        setLocationError(`Exact location nahi mil rahi (accuracy ~${Math.round(loc.acc)}m). Phone me GPS / "Precise location" on karo, khuli jagah par aao aur dobara try karo.`);
        setUserLocation(null);
        return;
      }
      setUserLocation(loc);
    } catch (geoErr) {
      setUserLocation(null);
      if (!geoExempt) {
        if (geoErr && geoErr.code === 1) {
          setLocationError('Location permission denied. Please allow location access in your browser settings.');
        } else {
          setLocationError('Location not available. Please turn on GPS (Precise location) and try again.');
        }
        return;
      }
    }

    // Step 1: Check if running on HTTPS
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      setCameraError('https');
      setDebugInfo('Page is not on HTTPS. Camera requires HTTPS.');
      return;
    }

    // Step 2: Check if mediaDevices API exists
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError('unsupported');
      setDebugInfo('navigator.mediaDevices not available. Browser: ' + navigator.userAgent.slice(0, 80));
      return;
    }

    // Step 3: Check permission status (if browser supports it)
    let permStatus = 'unknown';
    try {
      const perm = await navigator.permissions.query({ name: 'camera' });
      permStatus = perm.state; // 'granted', 'denied', or 'prompt'
      if (perm.state === 'denied') {
        setCameraError('denied');
        setDebugInfo('Camera permission is BLOCKED by browser. User must reset it in site settings.');
        return;
      }
    } catch (e) {
      // permissions.query not supported (Safari) — continue anyway
      permStatus = 'not-supported';
    }

    // Step 4: Request camera access
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } } });
    } catch (e1) {
      // Try without facingMode constraint
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      } catch (e2) {
        const err = e2 || e1;
        const name = err.name || '';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          setCameraError('denied');
          setDebugInfo(`Permission denied. Error: ${name}. Message: ${err.message || 'none'}. PermQuery: ${permStatus}`);
        } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          setCameraError('notfound');
          setDebugInfo(`No camera device found. Error: ${name}`);
        } else if (name === 'NotReadableError' || name === 'AbortError' || name === 'TrackStartError') {
          setCameraError('inuse');
          setDebugInfo(`Camera in use or hardware error. Error: ${name}. Message: ${err.message || 'none'}`);
        } else if (name === 'OverconstrainedError') {
          setCameraError('error');
          setDebugInfo(`Camera constraints not satisfied. Error: ${name}`);
        } else {
          setCameraError('error');
          setDebugInfo(`Unknown error. Name: ${name}. Message: ${err.message || 'none'}. Type: ${typeof err}`);
        }
        return;
      }
    }

    // Step 5: Permission granted — stop test stream
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
    }

    // Step 6: Show scanner UI first, then init scanner after DOM renders
    setScanning(true);
    setScannerPending(true);
  };

  // Initialize scanner AFTER React renders the qr-reader div
  useEffect(() => {
    if (!scannerPending || !scanning) return;
    setScannerPending(false);

    const initScanner = async () => {
      // Wait for DOM element to be available
      let retries = 0;
      while (!document.getElementById('qr-reader') && retries < 20) {
        await new Promise(resolve => setTimeout(resolve, 50));
        retries++;
      }
      if (!document.getElementById('qr-reader')) {
        setCameraError('error');
        setDebugInfo('QR reader element not found in DOM. Please refresh the page.');
        setScanning(false);
        return;
      }

      try {
        const scanner = new Html5Qrcode('qr-reader');
        scannerRef.current = scanner;

        let started = false;
        try {
          await scanner.start(
            { facingMode: 'environment' },
            { fps: 10, qrbox: { width: 250, height: 250 } },
            onScanSuccess,
            () => {}
          );
          started = true;
        } catch (e) {
          try {
            const devices = await Html5Qrcode.getCameras();
            if (devices && devices.length > 0) {
              await scanner.start(
                devices[devices.length - 1].id,
                { fps: 10, qrbox: { width: 250, height: 250 } },
                onScanSuccess,
                () => {}
              );
              started = true;
            }
          } catch (e2) {
            try {
              const devices2 = await Html5Qrcode.getCameras();
              if (devices2 && devices2.length > 0) {
                await scanner.start(
                  devices2[0].id,
                  { fps: 10, qrbox: { width: 250, height: 250 } },
                  onScanSuccess,
                  () => {}
                );
                started = true;
              }
            } catch (e3) { /* give up */ }
          }
        }

        if (!started) {
          setCameraError('error');
          setDebugInfo('Scanner could not start with any camera.');
          setScanning(false);
        }
      } catch (err) {
        setCameraError('error');
        setDebugInfo(`Scanner init error: ${err.message || err}`);
        setScanning(false);
      }
    };

    initScanner();
  }, [scannerPending, scanning]); // eslint-disable-line react-hooks/exhaustive-deps

  const onScanSuccess = async (decodedText) => {
    let token = decodedText;
    try {
      const url = new URL(decodedText);
      const t = url.searchParams.get('token');
      if (t) token = t;
    } catch (e) { /* not a URL, use raw text as token */ }

    try {
      if (scannerRef.current) await scannerRef.current.stop();
    } catch (e) { /* ignore */ }
    setScanning(false);

    setLoading(true);
    try {
      const payload = { token };
      if (userLocation) {
        payload.lat = userLocation.lat;
        payload.lng = userLocation.lng;
        payload.acc = userLocation.acc;
      }
      const res = await api.post('/staff/qr-checkin', payload);
      setResult(res.data);
      message.destroy();
      if (res.data.already) {
        message.info(res.data.message);
      } else {
        message.success(res.data.message);
      }
    } catch (err) {
      message.destroy();
      message.error(err.response?.data?.error || 'Failed to check in');
      setResult({ error: err.response?.data?.error || 'Failed' });
    } finally {
      setLoading(false);
    }
  };

  const stopScan = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (e) { /* ignore */ }
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => { if (scannerRef.current) { try { scannerRef.current.stop(); } catch (e) {} } };
  }, []);

  return (
    <div style={{ maxWidth: 500, margin: '0 auto', padding: 16 }}>
      <Title level={3} style={{ textAlign: 'center', marginBottom: 24 }}>
        <QrcodeOutlined /> QR Attendance
      </Title>

      {loading && !result && (
        <Card style={{ borderRadius: 16, textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <Title level={4} style={{ marginTop: 16 }}>Checking in...</Title>
        </Card>
      )}

      {!result && !scanning && !loading && (
        <Card style={{ borderRadius: 16, textAlign: 'center', padding: 24 }}>
          <CameraOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 16 }} />
          <Title level={4}>Scan Office QR Code</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
            First scan = Check-In (aane ka time)<br />
            Second scan = Check-Out (jaane ka time)
          </Text>
          <Button type="primary" size="large" icon={<CameraOutlined />} onClick={startScan}
            style={{ height: 50, fontSize: 18, borderRadius: 12 }}>
            Open Camera & Scan
          </Button>

          {cameraError === 'denied' && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 20, textAlign: 'left' }}
              message="Camera Permission Blocked"
              description={
                <div style={{ fontSize: 13 }}>
                  <p style={{ marginBottom: 8 }}><b>Browser ne camera block kar rakha hai.</b> Isko fix karne ke liye:</p>
                  <div style={{ background: '#f6f6f6', padding: 12, borderRadius: 8, marginBottom: 8 }}>
                    <p style={{ margin: '0 0 8px' }}><b>📱 Chrome (Android/Mobile):</b></p>
                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                      <li>Address bar me <b>🔒 lock icon</b> tap karo (URL ke left side)</li>
                      <li>"Permissions" ya "Site settings" tap karo</li>
                      <li><b>Camera</b> → <b>"Allow"</b> select karo</li>
                      <li>Page refresh karo aur dubara "Open Camera" dabao</li>
                    </ol>
                  </div>
                  <div style={{ background: '#f6f6f6', padding: 12, borderRadius: 8, marginBottom: 8 }}>
                    <p style={{ margin: '0 0 8px' }}><b>🍎 Safari (iPhone/iPad):</b></p>
                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                      <li>Phone ki <b>Settings</b> app kholo</li>
                      <li><b>Safari</b> → <b>Camera</b> → <b>"Allow"</b></li>
                      <li>Ya: address bar me <b>aA</b> tap → <b>Website Settings</b> → Camera → Allow</li>
                    </ol>
                  </div>
                  <div style={{ background: '#f6f6f6', padding: 12, borderRadius: 8 }}>
                    <p style={{ margin: '0 0 8px' }}><b>💻 Chrome (Desktop/Laptop):</b></p>
                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                      <li>Address bar me <b>🔒 lock icon</b> click karo</li>
                      <li>"Site settings" click karo</li>
                      <li><b>Camera</b> → <b>"Allow"</b> select karo</li>
                      <li>Tab close karke dubara kholo</li>
                    </ol>
                  </div>
                </div>
              }
            />
          )}

          {cameraError === 'https' && (
            <Alert type="error" showIcon style={{ marginTop: 20 }}
              message="HTTPS Required"
              description="Camera sirf HTTPS pe kaam karta hai. Please https://data.intubemedia.com use karo (http:// nahi)."
            />
          )}

          {cameraError === 'unsupported' && (
            <Alert type="warning" showIcon style={{ marginTop: 20 }}
              message="Browser not supported"
              description="Is browser me camera support nahi hai. Please Chrome ya Safari use karo."
            />
          )}

          {cameraError === 'notfound' && (
            <Alert type="warning" showIcon style={{ marginTop: 20 }}
              message="No Camera Found"
              description="Is device me camera nahi mila. Phone ya camera wale device se try karo."
            />
          )}

          {cameraError === 'inuse' && (
            <Alert type="warning" showIcon style={{ marginTop: 20 }}
              message="Camera Busy"
              description="Camera kisi aur app me use ho raha hai (jaise WhatsApp video call). Sab apps band karo aur dubara try karo."
            />
          )}

          {cameraError === 'error' && (
            <Alert type="error" showIcon style={{ marginTop: 20 }}
              message="Camera Error"
              description="Camera start nahi ho paya. Page refresh karo (Ctrl+Shift+R) aur dubara try karo."
            />
          )}

          {locationError && (
            <Alert type="error" showIcon style={{ marginTop: 20 }}
              message="Location Required"
              description={
                <div>
                  <p>{locationError}</p>
                  <p style={{ marginTop: 8, fontSize: 13 }}>
                    <b>Fix:</b> Go to Phone Settings → Turn on Location. Then allow location permission for this site in your browser.
                  </p>
                </div>
              }
            />
          )}

          {debugInfo && (
            <div style={{ marginTop: 12, padding: 8, background: '#f9f9f9', borderRadius: 6, fontSize: 11, color: '#999', textAlign: 'left', wordBreak: 'break-all' }}>
              <b>Debug:</b> {debugInfo}
            </div>
          )}
        </Card>
      )}

      {scanning && (
        <Card style={{ borderRadius: 16, textAlign: 'center' }}>
          <div id="qr-reader" ref={readerRef} style={{ width: '100%', marginBottom: 16 }} />
          <Button danger onClick={stopScan}>Cancel</Button>
        </Card>
      )}

      {result && !result.error && (
        <Result
          status="success"
          icon={result.type === 'checkout' ? <LogoutOutlined style={{ color: '#ff4d4f' }} /> : <CheckCircleOutlined />}
          title={result.type === 'checkout' ? 'Check-Out Done!' : result.already ? 'Already Checked In' : 'Check-In Done!'}
          subTitle={
            <Space direction="vertical" size={8}>
              <Text strong style={{ fontSize: 16 }}>{result.date}</Text>
              {result.check_in_time && (
                <Tag color="green" style={{ fontSize: 16, padding: '4px 12px' }}>
                  <ClockCircleOutlined /> In: {result.check_in_time}
                </Tag>
              )}
              {result.check_out_time && (
                <Tag color="red" style={{ fontSize: 16, padding: '4px 12px' }}>
                  <LogoutOutlined /> Out: {result.check_out_time}
                </Tag>
              )}
            </Space>
          }
          extra={
            <Space>
              <Button type="primary" size="large" onClick={() => navigate('/')}
                style={{ borderRadius: 10, height: 44 }}>
                OK - Dashboard
              </Button>
              <Button onClick={() => setResult(null)}
                style={{ borderRadius: 10, height: 44 }}>
                Scan Again
              </Button>
            </Space>
          }
        />
      )}

      {result && result.error && (
        <Result
          status="error"
          title="Scan Failed"
          subTitle={result.error}
          extra={<Button type="primary" onClick={() => setResult(null)}>Try Again</Button>}
        />
      )}
    </div>
  );
}
